import { BranchRecoveryDiagnostics } from "../Shared/Generated/BranchRecoveryDiagnostics";
import { BranchFailurePhase } from "../Shared/Generated/BranchFailurePhase";
import { BranchMatchStatus } from "../Shared/Generated/BranchMatchStatus";
import { BranchDiagnosticValueKind } from "../Shared/Generated/BranchDiagnosticValueKind";
import { BranchClickedStatus } from "../Shared/Generated/BranchClickedStatus";
import { BranchReferrerStatus } from "../Shared/Generated/BranchReferrerStatus";
import { BranchSourceStatus } from "../Shared/Generated/BranchSourceStatus";
import { BranchSdkErrorType } from "../Shared/Generated/BranchSdkErrorType";
import { BranchPageVisibility } from "../Shared/Generated/BranchPageVisibility";
import { BranchNavigationType } from "../Shared/Generated/BranchNavigationType";
import { BranchGuardOutcome } from "../Shared/Generated/BranchGuardOutcome";

export const boundedDuration = (value: number): number => Number.isFinite(value) ? Math.min(120000, Math.max(0, Math.round(value))) : 0;
export const httpStatus = (value: unknown): number | null => typeof value === "number" && Number.isInteger(value) && value >= 100 && value <= 599 ? value : null;
export const diagnosticKind = (value: unknown): BranchDiagnosticValueKind => value === undefined ? BranchDiagnosticValueKind.Missing :
    value === null ? BranchDiagnosticValueKind.Null : Array.isArray(value) ? BranchDiagnosticValueKind.Array :
    typeof value === "object" ? BranchDiagnosticValueKind.Object : typeof value === "string" ? BranchDiagnosticValueKind.String :
    typeof value === "number" ? BranchDiagnosticValueKind.Number : typeof value === "boolean" ? BranchDiagnosticValueKind.Boolean : BranchDiagnosticValueKind.Other;

export function newBranchDiagnostics(): BranchRecoveryDiagnostics {
    return {
        version: 1, phase: BranchFailurePhase.Startup, elapsedMs: 0, sdkElapsedMs: null, retryNumber: 0, resolveRequestCount: 0,
        online: navigator.onLine, visibility: BranchPageVisibility.Unknown, navigationType: BranchNavigationType.Unknown,
        resolveRequests: [], wasHidden: document.visibilityState === "hidden",
        sdkAvailable: false, sdkAttempted: false, sdkCallbackReceived: false, matchStatus: BranchMatchStatus.NotChecked, matchCount: 0,
        parsedDataKind: BranchDiagnosticValueKind.Missing, parsedKeyCount: 0, clickedStatus: BranchClickedStatus.NotChecked,
        topReferrerKind: BranchDiagnosticValueKind.Missing, nestedReferrerKind: BranchDiagnosticValueKind.Missing,
        referrerStatus: BranchReferrerStatus.NotChecked, sourceStatus: BranchSourceStatus.NotChecked, sdkErrorType: BranchSdkErrorType.None,
        sdkHttpStatus: null, resolveHttpStatus: null, sdkResourceSeen: false, sdkResourceDurationMs: null, sdkResourceStatus: null,
        matchResourceSeen: false, matchResourceDurationMs: null, matchResourceStatus: null,
        openResourceSeen: false, openResourceDurationMs: null, openResourceStatus: null,
        storageAvailable: null, guardOutcome: BranchGuardOutcome.NotChecked, clientErrorType: BranchSdkErrorType.None
    };
}

export function captureBrowserDiagnostics(value: BranchRecoveryDiagnostics, startedAt = 0): void {
    value.online = navigator.onLine;
    value.visibility = document.visibilityState === "visible" ? BranchPageVisibility.Visible : document.visibilityState === "hidden" ? BranchPageVisibility.Hidden : BranchPageVisibility.Unknown;
    if (typeof performance.getEntriesByType !== "function") return;
    const navigation = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
    value.navigationType = navigation?.type === "navigate" ? BranchNavigationType.Navigate : navigation?.type === "reload" ? BranchNavigationType.Reload :
        navigation?.type === "back_forward" ? BranchNavigationType.BackForward : BranchNavigationType.Unknown;
    const resources = performance.getEntriesByType("resource") as PerformanceResourceTiming[];
    const findResource = (test: (url: URL) => boolean, currentAttempt = false) => {
        const matches = resources.filter(entry => {
            try { return (!currentAttempt || entry.startTime >= startedAt) && test(new URL(entry.name)); } catch { return false; }
        });
        return matches[matches.length - 1];
    };
    const sdkResource = findResource(url => url.origin === location.origin && url.pathname.startsWith("/lib/branch/2.89.5/"));
    value.sdkResourceSeen = !!sdkResource;
    value.sdkResourceDurationMs = sdkResource ? boundedDuration(sdkResource.duration) : null;
    value.sdkResourceStatus = sdkResource ? httpStatus((sdkResource as PerformanceResourceTiming & { responseStatus?: number }).responseStatus) : null;
    const matchResource = findResource(url => url.origin === "https://app.link" && url.pathname === "/_r", true);
    value.matchResourceSeen = !!matchResource;
    value.matchResourceDurationMs = matchResource ? boundedDuration(matchResource.duration) : null;
    value.matchResourceStatus = matchResource ? httpStatus((matchResource as PerformanceResourceTiming & { responseStatus?: number }).responseStatus) : null;
    const openResource = findResource(url => url.origin === "https://api2.branch.io" && url.pathname === "/v1/open", true);
    value.openResourceSeen = !!openResource;
    value.openResourceDurationMs = openResource ? boundedDuration(openResource.duration) : null;
    value.openResourceStatus = openResource ? httpStatus((openResource as PerformanceResourceTiming & { responseStatus?: number }).responseStatus) : null;
}

export function captureMatchContext(value: BranchRecoveryDiagnostics): string[] {
    const matches = new URL(location.href).searchParams.getAll("_branch_match_id");
    value.matchCount = Math.min(3, matches.length);
    value.matchStatus = matches.length === 0 ? BranchMatchStatus.Missing : matches.length !== 1 ? BranchMatchStatus.Repeated :
        !matches[0] ? BranchMatchStatus.Empty : matches[0].length > 2048 ? BranchMatchStatus.TooLong : BranchMatchStatus.Valid;
    return matches;
}

export function captureSdkError(error: unknown, diagnostics: BranchRecoveryDiagnostics): void {
    diagnostics.sdkErrorType = clientErrorType(error);
    try {
        if (error && typeof error === "object") {
            const values = error as Record<string, unknown>;
            diagnostics.sdkHttpStatus = httpStatus(values.status) ?? httpStatus(values.statusCode) ?? httpStatus(values.httpStatus);
            // The pinned SDK exposes this exact literal for its own transport timeout.
            if (values.message === "Request timed out") diagnostics.sdkErrorType = BranchSdkErrorType.TimeoutError;
        }
    } catch { diagnostics.sdkHttpStatus = null; }
}

export function clientErrorType(error: unknown): BranchSdkErrorType {
    if (!error || typeof error !== "object") return BranchSdkErrorType.Other;
    try {
        const name = (error as { name?: unknown }).name;
        return typeof name === "string" && name !== BranchSdkErrorType.None && Object.values(BranchSdkErrorType).includes(name as BranchSdkErrorType)
            ? name as BranchSdkErrorType : BranchSdkErrorType.Other;
    } catch { return BranchSdkErrorType.Other; }
}
